import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { getBearerToken, userManager } from './oidc';
import { useNotificationStore } from '../stores/notifications';

// Auth is now OIDC (auth-code + PKCE) against the redwolffoundry IdP. Every request
// carries the OIDC id_token as a Bearer; the Worker verifies it against the IdP's
// JWKS. No more native-Bearer-from-password or web cookie/CSRF transports — the
// id_token is used uniformly on web and native.
const apiClient = axios.create({
	baseURL: process.env.VUE_APP_API_URL || 'http://localhost:3000',
	timeout: 10000,
	headers: {
		'Content-Type': 'application/json'
	}
});

// Lazy-load notification store (no circular dependency issue)
let notificationStore = null;
function getNotificationStore() {
	if (!notificationStore) {
		notificationStore = useNotificationStore();
	}
	return notificationStore;
}

// Attach the OIDC id_token as the Bearer on every request (skipped when none —
// e.g. before sign-in).
apiClient.interceptors.request.use(
	async (config) => {
		const token = await getBearerToken();
		if (token) config.headers.Authorization = `Bearer ${token}`;
		return config;
	},
	(error) => Promise.reject(error)
);

// On 401, start a fresh OIDC sign-in (token missing/expired) rather than routing
// to a Login view. Other errors surface a notification as before.
let redirecting = false;
apiClient.interceptors.response.use(
	(response) => response,
	(error) => {
		const notification = getNotificationStore();

		if (error.response) {
			const { status, data } = error.response;
			const errorMessage = data?.message || data?.error || data?.reason;

			if (status === 401) {
				if (!redirecting) {
					redirecting = true;
					if (Capacitor.isNativePlatform()) {
						// A same-window signinRedirect would navigate the WebView itself to the IdP
						// with a custom-scheme redirect and no deep-link listener → dead end. Clear
						// the stale session and send the user to /login, which drives the native
						// external-browser sign-in on tap.
						notification.showError('Your session has expired. Please sign in again.');
						userManager.removeUser().finally(() => window.location.assign('/login'));
					} else {
						notification.showError('Your session has expired. Signing you in again…');
						// Reset the latch if the redirect itself fails (e.g. IdP discovery down),
						// else every later 401 is silently swallowed until a full reload.
						userManager
							.signinRedirect({ state: { redirect: window.location.pathname } })
							.catch(() => {
								redirecting = false;
							});
					}
				}
			} else if (status === 403) {
				notification.showError(errorMessage || 'You do not have permission to perform this action.');
			} else if (status === 404) {
				notification.showError(errorMessage || 'The requested resource was not found.');
			} else if (status === 500) {
				notification.showError('A server error occurred. Please try again later.');
			} else if (errorMessage) {
				notification.showError(errorMessage);
			}
		} else if (error.request) {
			notification.showError('Unable to connect to the server. Please check your connection.');
		} else {
			notification.showError('An unexpected error occurred.');
		}

		return Promise.reject(error);
	}
);

// Build the public URL for an uploaded image key (served by GET /images/<key>).
// Returns null for a falsy key so callers can fall back to initials/icons.
export function imageUrl(key) {
	if (!key) return null;
	return `${apiClient.defaults.baseURL}/images/${key}`;
}

export default {
	// Image endpoints
	async uploadImage(dataUrl) {
		// POST a data URL; the Worker stores it and returns { key: "<uuid>.jpg" }.
		const response = await apiClient.post('/images', { dataUrl });
		return response.data;
	},

	// User endpoints
	getUsers() {
		return apiClient.get('/user');
	},
	getCurrentUser() {
		const userId = localStorage.getItem('userId');
		return apiClient.get(`/user/${userId}`);
	},
	updateUser(userData) {
		const userId = localStorage.getItem('userId');
		return apiClient.put(`/user/${userId}`, userData);
	},
	patchUser(userData) {
		// Partial update (e.g. { avatar }); a user may only patch their own record.
		const userId = localStorage.getItem('userId');
		return apiClient.patch(`/user/${userId}`, userData);
	},

	// Circle endpoints
	getCircles() {
		// Populate memberships relation with user and role details
		return apiClient.get('/circle?memberships.fields=id,user,role&memberships.user.fields=id,name,email');
	},
	getCircle(id) {
		// Populate memberships relation with user and role details
		return apiClient.get(`/circle/${id}?memberships.fields=id,user,role&memberships.user.fields=id,name,email`);
	},
	createCircle(circleData) {
		return apiClient.post('/circle', circleData);
	},
	updateCircle(id, circleData) {
		return apiClient.patch(`/circle/${id}`, circleData);
	},
	deleteCircle(id) {
		return apiClient.delete(`/circle/${id}`);
	},

	// Restaurant endpoints
	getRestaurants(params = {}) {
		const queryParams = new URLSearchParams();
		if (params.circleId) {
			// Use eq operator for filtering by circle
			queryParams.set('where', JSON.stringify({ circle: { eq: params.circleId } }));
		}
		const queryString = queryParams.toString();
		return apiClient.get(`/restaurant${queryString ? '?' + queryString : ''}`);
	},
	getRestaurant(id) {
		return apiClient.get(`/restaurant/${id}`);
	},
	createRestaurant(restaurantData) {
		return apiClient.post('/restaurant', restaurantData);
	},
	updateRestaurant(id, restaurantData) {
		return apiClient.put(`/restaurant/${id}`, restaurantData);
	},
	deleteRestaurant(id) {
		return apiClient.delete(`/restaurant/${id}`);
	},

	// Visit endpoints
	getVisits(params = {}) {
		const queryParams = new URLSearchParams();
		// Expand attended user data with names
		queryParams.set('attended.fields', 'id,name,email');
		if (params.circleId) {
			// Use eq operator for filtering: {circle: {eq: "uuid"}}
			queryParams.set('where', JSON.stringify({ circle: { eq: params.circleId } }));
		}
		return apiClient.get(`/visit?${queryParams.toString()}`);
	},
	getVisit(id) {
		return apiClient.get(`/visit/${id}?attended.fields=id,name,email`);
	},
	createVisit(visitData) {
		return apiClient.post('/visit', visitData);
	},
	updateVisit(id, visitData) {
		return apiClient.put(`/visit/${id}`, visitData);
	},
	deleteVisit(id) {
		return apiClient.delete(`/visit/${id}`);
	},

	// Review endpoints
	getReviews() {
		return apiClient.get('/review?user.fields=id,name,email');
	},
	getReview(id) {
		return apiClient.get(`/review/${id}?user.fields=id,name,email`);
	},
	createReview(reviewData) {
		return apiClient.post('/review', reviewData);
	},
	updateReview(id, reviewData) {
		return apiClient.put(`/review/${id}`, reviewData);
	},
	deleteReview(id) {
		return apiClient.delete(`/review/${id}`);
	},

	// Invitation endpoints
	getInvitations() {
		return apiClient.get('/invitation?circle.fields=id,name');
	},
	getInvitation(id) {
		return apiClient.get(`/invitation/${id}`);
	},
	createInvitation(invitationData) {
		return apiClient.post('/invitation', invitationData);
	},
	updateInvitation(id, invitationData) {
		return apiClient.put(`/invitation/${id}`, invitationData);
	},
	deleteInvitation(id) {
		return apiClient.delete(`/invitation/${id}`);
	},

	// CircleMember endpoints
	getCircleMembers(circleId) {
		const params = new URLSearchParams();
		params.set('where', JSON.stringify({ circle: { eq: circleId } }));
		params.set('user.fields', 'id,name,email,avatar');
		return apiClient.get(`/circleMember?${params.toString()}`);
	},
	createCircleMember(memberData) {
		return apiClient.post('/circleMember', memberData);
	},
	updateCircleMember(id, memberData) {
		return apiClient.patch(`/circleMember/${id}`, memberData);
	},
	deleteCircleMember(id) {
		return apiClient.delete(`/circleMember/${id}`);
	},

	// Generic reorder endpoint for orderable relations
	reorderRelation(model, id, relation, orderedIds) {
		return apiClient.post(`/${model}/${id}/reorder/${relation}`, { ids: orderedIds });
	},

	// Google Places endpoints
	searchGooglePlaces(query, options = {}) {
		const params = new URLSearchParams({ query });
		if (options.location) params.set('location', options.location);
		if (options.radius) params.set('radius', options.radius);
		if (options.type) params.set('type', options.type);
		return apiClient.get(`/google-places/search?${params.toString()}`);
	},
	getGooglePlaceDetails(placeId) {
		return apiClient.get(`/google-places/details?placeId=${encodeURIComponent(placeId)}`);
	},
	getGooglePlacePhotoBlob(name, { maxWidthPx, maxHeightPx } = {}) {
		const params = new URLSearchParams({ name });
		if (maxWidthPx) params.set('maxWidthPx', String(maxWidthPx));
		if (maxHeightPx) params.set('maxHeightPx', String(maxHeightPx));
		// Returns image bytes proxied from Google (key stays server-side). The
		// id_token Bearer is attached by the request interceptor; the stable URL
		// lets the browser HTTP cache serve repeat requests.
		return apiClient.get(`/google-places/photo?${params.toString()}`, { responseType: 'blob' });
	}
};
